import React, { useState, useEffect, useCallback, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Alert, Button, Table } from "react-bootstrap";
import axios from "axios";
import LabelFieldComponent from "../../../../components/fields/LabelFieldComponent";
import PageLayout from "../../../../layouts/PageLayout";

export default function StockKeeperIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stockKeepers, setStockKeepers] = useState([]);
    const [search, setSearch] = useState("");

    const fetchStockKeepers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/stock-keeper`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            if (response.data?.success) {
                setStockKeepers(response.data.data || []);
            } else {
                setError(response.data?.message || "Failed to fetch stock keepers.");
            }
        } catch (err) {
            console.error("Error fetching stock keepers:", err);
            setError(err.response?.data?.message || "Failed to fetch stock keepers list.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStockKeepers();
    }, [fetchStockKeepers]);

    const filteredStockKeepers = stockKeepers.filter((sk) => {
        const q = search.toLowerCase();
        return (
            sk.name?.toLowerCase().includes(q) ||
            sk.email?.toLowerCase().includes(q) ||
            sk.nickname?.toLowerCase().includes(q)
        );
    });

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {t("Stock Keepers")} ({filteredStockKeepers.length})
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">{t("Stock Keepers")}</li>
                                </ul>
                            </div>
                            <Button
                                variant="primary"
                                onClick={() => navigate("/admin/stock-keeper/create")}
                            >
                                <i className="material-icons me-1 vertical-middle">add</i>
                                {t("Add Stock Keeper")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card p-4 rounded-4 shadow-sm border-0 bg-white">
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError("")} className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Row className="g-3 align-items-end mb-4">
                            <Col md={6} xl={4}>
                                <LabelFieldComponent
                                    type="search"
                                    placeholder="Search by Name, Email or Nickname..."
                                    label="Search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">{t("Loading stock keepers...")}</p>
                            </div>
                        ) : (
                            <div className="mc-table-responsive">
                                <Table hover className="mc-table align-middle">
                                    <thead className="mc-table-head primary">
                                        <tr>
                                            <th>Name</th>
                                            <th>Nickname</th>
                                            <th>Phone</th>
                                            <th>Opening Balance</th>
                                            <th>Salary</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="mc-table-body">
                                        {filteredStockKeepers.length > 0 ? (
                                            filteredStockKeepers.map((sk) => (
                                                <tr key={sk.id}>
                                                    <td className="fw-semibold">{sk.name || "N/A"}</td>
                                                    <td>{sk.nickname || "N/A"}</td>
                                                    <td>{sk.phone || "N/A"}</td>
                                                    <td>{sk.opening_balance ?? "0"}</td>
                                                    <td>{sk.salary ?? "0"}</td>
                                                    <td>
                                                        <span className={`badge ${sk.status ? "bg-success" : "bg-secondary"}`}>
                                                            {sk.status ? "Active" : "Inactive"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="mc-table-action d-flex align-items-center gap-2">
                                                            <button
                                                                type="button"
                                                                className="material-icons border-0 bg-transparent text-primary"
                                                                onClick={() => navigate(`/admin/stock-keeper/edit/${sk.id}`, { state: { stockKeeper: sk } })}
                                                                title="Edit"
                                                            >
                                                                edit
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className="text-center py-4 text-muted">
                                                    {t("No stock keepers found")}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}